import { Box, Divider, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import CardGrid from '../../../components/cards/layout/CardGrid';
import SkeletonCard from '../../../components/cards/SkeletonCard';
import {
  cloneElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { API_DELETE, API_GET, DBRequest, useConfig } from '../../../api/api';
import TextCard from '../../../components/cards/TextCard';
import SpeedDialMenu from '../../../components/menu/SpeedDialMenu';
import PopUpContainer from '../../../components/popup/PopUpContainer';
import Share from '../../../components/special/Share';
import Feedback from '../../../components/special/Feedback';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoCard from '../../../components/cards/InfoCard';
import MoneyCard from '../../../components/cards/MoneyCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FileCard from '../../../components/cards/FilesCard';
import DownloadIcon from '@mui/icons-material/Download';
import FilePreviewCard from '../../../components/cards/FilePreviewCard';
import CardContainer from '../../../components/cards/layout/CardContainer';
import { costCenterIconMap } from '../../../components/exo/ExoCostCenter';
import MoneyLabel from '../../../components/label/MoneyLabel';
import RenderHTML from '../../../components/preview/RenderHTML';
import LabeledBox from '../../../components/special/LabeledBox';
import ExoTable from '../../../components/exo/ExoTable';
import { useNavigate } from 'react-router-dom';
import DateLabel from '../../../components/label/DateLabel';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';

const ExtendRecurringReceipt = forwardRef(
  ({ row, onUpdate, onClickEdit, onDelete, skr03 }, ref) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const { t } = useTranslation();
    const config = useConfig();
    const navigate = useNavigate();

    const [recurringReceipt, setRecurringReceipt] = useState(null);

    // exposed functions
    useImperativeHandle(ref, () => ({
      setData: setRecurringReceipt,
    }));

    useEffect(() => {
      if (!config) return;
      DBRequest({
        config,
        path: `recurring-receipts/${row.id}`,
        method: API_GET,
        onResponse: handleResponse,
      });
    }, [config, row]);

    function handleResponse(data) {
      setRecurringReceipt(data.data);
    }

    const Columns = [
      {
        header: t('Nr'), // header of column
        key: 'receiptNr', // key of column in row-object
        thAlign: 'left', // header text alignment
        tdAlign: 'left', // cell text alignment
        sortable: true, // enable sort function for column
        width: '60px',
        hiddenMobile: false, // hides the column on mobile
        onClick: (row) => navigate(`/expenses/receipts/${row.id}`),
      },
      {
        header: t('Title'), // header of column
        key: 'title', // key of column in row-object
        thAlign: 'left', // header text alignment
        tdAlign: 'left', // cell text alignment
        sortable: true, // enable sort function for column
        hiddenMobile: false, // hides the column on mobile
        onClick: (row) => navigate(`/expenses/receipts/${row.id}`),
      },
      {
        header: t('Receipt Date'), // header of column
        key: 'endDate', // key of column in row-object
        thAlign: 'right', // header text alignment
        tdAlign: 'right', // cell text alignment
        sortable: true, // enable sort function for column
        width: '150px', // width of column
        cell: ({ row }) => (
          <Box className="monospace">
            <DateLabel date={row.receiptDate} />
          </Box>
        ), // custom xml for cells in the column. you get the row as object
        hiddenMobile: true, // hides the column on mobile
      },
      {
        header: t('Open amount'), // header of column
        key: 'openAmount', // key of column in row-object
        thAlign: 'right', // header text alignment
        tdAlign: 'right', // cell text alignment
        sortable: true, // enable sort function for column
        width: '140px', // width of column
        cell: ({ row }) => (
          <Typography
            className="monospace"
            color={
              Math.round(parseFloat(row.openAmount) * 100) / 100 > 0
                ? colors.error
                : colors.success
            }
            fontWeight={700}
          >
            {Math.round(parseFloat(row.openAmount) * 100) / 100 === 0 ? (
              <PriceCheckIcon />
            ) : (
              <MoneyLabel money={row.openAmount} />
            )}
          </Typography>
        ),
      },
    ];

    // menu
    const actions = [
      {
        icon: <ModeEditIcon />,
        name: t('Edit'),
        onClick: () => onClickEdit(recurringReceipt),
      },
      {
        icon: <ContentCopyIcon />,
        name: t('Copy'),
        onClick: handleCopy,
      },
      {
        icon: <PrintIcon />,
        name: t('Print'),
        onClick: () =>
          handlePrint(recurringReceipt && recurringReceipt.file.temporaryUrl),
        hidden: recurringReceipt && recurringReceipt.file ? false : true,
      },
      {
        icon: <ShareIcon />,
        name: t('Share'),
        onClick: () => setOpenShare(true),
      },
      { icon: <DeleteOutlineIcon />, name: t('Delete'), onClick: handleDelete },
    ];

    function handleCopy() {
      const { id, ...dataCopied } = recurringReceipt;

      onClickEdit(dataCopied);
    }

    // share
    const [openShare, setOpenShare] = useState(false);

    // print
    function handlePrint(pdfUrl) {
      // Open a new window or iframe with the PDF URL
      const printWindow = window.open(pdfUrl, '_blank');

      // Wait for the PDF to load
      printWindow.onload = () => {
        // Trigger the print dialog
        printWindow.print();
      };
    }

    // delete
    function handleDelete() {
      DBRequest({
        config,
        path: `recurring-receipts/${recurringReceipt.id}`,
        method: API_DELETE(),
        onFeedback: handleAlertOpen,
        onResponse: (res) => onDelete(row),
      });
    }

    // feedback
    const [alertState, setAlertState] = useState({
      alertOpen: false,
      alertType: 'info',
      alertText: 'test',
    });
    function handleAlertOpen(text, type) {
      setAlertState({ alertOpen: true, alertText: text, alertType: type });
    }

    if (!recurringReceipt) return <ReceiptSkeleton />;

    const billingPeriodOptions = [
      { label: t('Monthly'), value: 1 },
      { label: t('Bimonthly'), value: 2 },
      { label: t('Quarterly'), value: 3 },
      { label: t('Semiannual'), value: 6 },
      { label: t('Annual'), value: 12 },
    ];

    const billingPeriodLabel = billingPeriodOptions.find(
      (opt) => opt.value === recurringReceipt.billingPeriod
    )?.label;

    let infos = [
      {
        label: t('Receipt Number'),
        value: recurringReceipt.receiptNr || '-',
        type: 'number',
      },
      {
        label: t('Receipt Date'),
        value: recurringReceipt.receiptDate,
        type: 'date',
      },
      {
        label: t('Due Date'),
        value: recurringReceipt.dueDate,
        type: 'date',
      },
      {
        label: t('Origin'),
        value: t(recurringReceipt.origin),
      },
      {
        label: t('Billing Period'),
        value: billingPeriodLabel,
      },
    ];

    if (recurringReceipt.seller)
      infos = infos.concat({
        label: t('Seller'),
        value: recurringReceipt.seller.name,
      });

    const files = recurringReceipt.file
      ? [
          {
            label: t('Receipt'),
            url: recurringReceipt.file.temporaryUrl,
            icon: <DownloadIcon />,
          },
        ]
      : [];

    return (
      <CardGrid>
        <TextCard text={recurringReceipt.title} span={5} />
        <FilePreviewCard file={recurringReceipt.file} />
        <InfoCard infos={infos} />

        <MoneyCard
          amount={recurringReceipt.amount}
          Icon={AttachMoneyIcon}
          title={t('Costs')}
          // subTitle={
          //   <Typography variant="h6">
          //     <b>
          //       <MoneyLabel money={recurringReceipt.openAmount} />
          //     </b>{' '}
          //     {t('open amount')}
          //   </Typography>
          // }
        />

        <FileCard files={files} />
        {recurringReceipt.positions && (
          <CardContainer span={3} className="flex flex-col gap-4">
            {recurringReceipt.positions.map((element) => (
              <RecurringReceiptPosition
                key={element.id}
                element={element}
                onClick={() => {}}
                noBackground
              />
            ))}
          </CardContainer>
        )}

        {recurringReceipt.seller && (
          <CardContainer>
            <Box className="w-full h-full flex justify-center items-center gap-2 flex-wrap">
              <Typography>{recurringReceipt?.seller.datevId}</Typography>
              <Typography>{recurringReceipt?.seller.name}</Typography>
            </Box>
          </CardContainer>
        )}

        <CardContainer
          className="max-h-[650px] min-h-[300px] relative"
          sx={{ padding: '0px' }}
          span={5}
          noBackground
        >
          <ExoTable
            className="w-full"
            data={{ data: recurringReceipt?.receipts }}
            isLoading={Boolean(!recurringReceipt.receipts)}
            columns={Columns}
          />
        </CardContainer>

        <PopUpContainer open={openShare} onClose={() => setOpenShare(false)}>
          <Share
            title={`${t('Share Recurring Receipt')} ${recurringReceipt.id}`}
            url={
              recurringReceipt.file ? recurringReceipt.file.temporaryUrl : ''
            }
          />
        </PopUpContainer>
        <SpeedDialMenu actions={actions} />
        <Feedback setState={setAlertState} state={alertState} />
      </CardGrid>
    );
  }
);
const ReceiptSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={52} span={5} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={156} span={4} />
    </CardGrid>
  );
};

export default ExtendRecurringReceipt;

export const RecurringReceiptPosition = ({
  element,
  onClick,
  noBackground,
  validationSchema,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const { taxAssignment, costCenter, amount, position, taxRate, note } =
    element;

  // Project the cost center belongs to. A cost center can be linked to several
  // projects; show the first one as "<nr> · <name>" if present. Indirect cost
  // centers are shared across projects, so we don't show a project for them.
  const project =
    costCenter?.type !== 'INDIRECT_COSTS' ? costCenter?.projects?.[0] : null;
  const projectInfo = project
    ? [project.nr, project.name].filter(Boolean).join(' · ')
    : '';

  const [errors, setErrors] = useState(false);

  useEffect(() => {
    validateData();
  }, [element]);

  // Function to validate dataCollection
  const validateData = async () => {
    try {
      // Validate the dataCollection
      await validationSchema.validate(element, { abortEarly: false });
      // If validation passes, update isValid to true
      setErrors(null);
    } catch (err) {
      // If validation fails, update isValid to false
      setErrors(err.errors);
      console.error('Validation errors:', err.errors);
    }
  };

  return (
    <LabeledBox
      className="flex flex-col @container gap-4 w-full rounded-lg"
      onClick={() => onClick(element)}
      noBackground={noBackground}
      outerSx={{
        backgroundColor: errors ? colors.warning + '40' : colors.bgInput,
      }}
    >
      <Box className="flex justify-between flex-col @md:flex-row @md:gap-6">
        <Box className="flex flex-col gap-1">
          {errors && (
            <Box className="flex flex-col gap-1">
              {errors?.map((obj) => (
                <Typography fontSize={'13px'} color={colors.warning}>
                  {obj}
                </Typography>
              ))}
            </Box>
          )}
          <Typography fontWeight={600}>{position}</Typography>
          {taxAssignment && (
            <Typography
              className="monospace"
              variant="h6"
            >{`${taxAssignment.account} ${taxAssignment.label}`}</Typography>
          )}

          {costCenter && (
            <Box className="flex items-center gap-2 pt-1">
              {costCenterIconMap[costCenter.type] &&
                cloneElement(costCenterIconMap[costCenter.type], {
                  size: '22px',
                })}
              <Box className="flex flex-col">
                <Typography>{costCenter.name}</Typography>
                {projectInfo && (
                  <Typography variant="h6" sx={{ opacity: 0.6 }}>
                    {projectInfo}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <Box>
          <Typography
            fontWeight={600}
            variant="h4"
            color={parseFloat(amount) > 0 ? 'error' : 'success'}
          >
            <MoneyLabel money={amount} />
          </Typography>
          <Typography
            variant="h6"
            className="monospace"
          >{`${taxRate} %`}</Typography>
        </Box>
      </Box>
      {note && (
        <>
          <Divider />
          <Box>
            <Typography variant="h6">{t('Note')}</Typography>
            <RenderHTML HTML={note || ' '} />
          </Box>
        </>
      )}
    </LabeledBox>
  );
};
